import { CONSULTATION_LANDING_STEP, CONSULTATION_STEPS, OTHER_SERVICE, PostMessageType } from '@abiglobalhealth/go-types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cancelConsultation } from '@/api/consultations'
import { getInitialModule } from '@/api/modules'
import { finishHealthNavigator } from '@/api/navigator'
import { resetTraces } from '@/api/traces'
import { ConfirmationModal } from '@/components/ConfirmationModal'
import { Close } from '@/components/icons/Close'
import { CloseConsultationIcon } from '@/components/icons/CloseConsultationIcon'
import { RefreshIcon } from '@/components/icons/RefreshIcon'
import { useConsultation } from '@/hooks/useConsultation'
import { useConsultationEnded } from '@/hooks/useConsultationEnded'
import { useConsultationStep } from '@/hooks/useConsultationStep'
import { useConsultationType } from '@/hooks/useConsultationType'
import { useIndications } from '@/hooks/useIndications'
import useLimitExceeded from '@/hooks/useLimitExceeded'
import { useLoading } from '@/hooks/useLoading'
import { useModule } from '@/hooks/useModule'
import useNavigator from '@/hooks/useNavigator'
import { useNavOnly } from '@/hooks/useNavOnly'
import { usePharmacy } from '@/hooks/usePharmacy'
import { usePostMessage } from '@/hooks/usePostMessage'
import { useSurveyCategories } from '@/hooks/useSurveyCategories'
import { useTraces } from '@/hooks/useTraces'
import { useUser } from '@/hooks/useUser'
import { isAbortError } from '@/utils/errors'
import { formatTrace } from '@/utils/traces'

export const CancelConsultationButton = () => {
  const { t } = useTranslation()
  const { showLoading, hideLoading, loading } = useLoading()
  const { user } = useUser()
  const { updateStep } = useConsultationStep()
  const { updateIndicationCategory } = useSurveyCategories()
  const { consultationType } = useConsultationType()
  const { updateModule } = useModule()
  const { clearIndications } = useIndications()
  const { clearSelectedPharmacy } = usePharmacy()
  const { updateConsultation } = useConsultation()
  const { setTraces } = useTraces()
  const { checkLimitExceeded } = useLimitExceeded()
  const consultationEnded = useConsultationEnded()
  const { setNavigator, loading: isNavigatorLoading, navigator, handleConversationEnded } = useNavigator()
  const { sendPostMessage } = usePostMessage()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  const { isNavOnly, returnToNavigator } = useNavOnly()

  const handleCancelConsultation = async () => {
    try {
      showLoading()

      abortControllerRef.current = new AbortController()
      const signal = abortControllerRef.current.signal

      await sendPostMessage(PostMessageType.CLOSE_CLICK, {
        consultationType
      })
      if (consultationType === OTHER_SERVICE.HEALTH_NAVIGATOR) {
        if (!navigator.healthNavigator.messages.length) return await handleConversationEnded()

        const navigatorAnswer = await finishHealthNavigator(
          {
            uniqueId: user?.data?.uniqueId!
          },
          signal
        )
        await sendPostMessage(PostMessageType.NAVIGATOR_FINISHED, {
          conversationId: navigator.healthNavigator.conversationId! // Use finished navigator conversation ID, not the response one (it's empty)
        })
        await sendPostMessage(PostMessageType.NAVIGATOR_MESSAGE, {
          ...navigatorAnswer,
          conversationId: navigatorAnswer.healthNavigator.conversationId!
        })
        await handleConversationEnded()
        setNavigator(navigatorAnswer)
      } else if (consultationEnded) {
        console.warn('Consultation already ended, resetting traces without canceling consultation')
        // If consultation is ended, reset traces (i.e. form/indications before creating a new consultation)
        await resetTraces(
          {
            uniqueId: user?.data?.uniqueId!
          },
          signal
        )
      } else {
        // Only cancel consultation if it's not ended
        const canceledConsultation = await cancelConsultation(
          {
            uniqueId: user?.data?.uniqueId as string
          },
          signal
        )
        updateConsultation(canceledConsultation)
      }

      if (isNavOnly) return returnToNavigator()

      const { module } = await getInitialModule({
        uniqueId: user?.data?.uniqueId!,
        userId: user?.data?.userId!,
        serviceType: consultationType
      })
      updateModule(module)
      await checkLimitExceeded(consultationType)
      updateIndicationCategory(null)
      clearIndications()
      clearSelectedPharmacy()

      setTraces([formatTrace(module.id, CONSULTATION_LANDING_STEP.id)])
      updateStep(CONSULTATION_STEPS.LANDING)
    } catch (error: unknown) {
      if (!isAbortError(error)) throw error
    } finally {
      setIsModalOpen(false)
      hideLoading()
      abortControllerRef.current = null
    }
  }

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
  }, [])

  return (
    <>
      <ConfirmationModal
        onCancel={() => setIsModalOpen(false)}
        onSuccess={handleCancelConsultation}
        isOpen={isModalOpen}
        header={{
          icon: <CloseConsultationIcon className="text-primary mx-auto h-16 w-16" />,
          title: t([`general.consultation.modal.are_you_sure.${consultationType}`, 'general.consultation.modal.are_you_sure'])
        }}
      >
        {consultationType === OTHER_SERVICE.HEALTH_NAVIGATOR && <p className="text-center">{t('health_navigator.close_conversation.description')}</p>}
      </ConfirmationModal>

      <button
        onClick={() => setIsModalOpen(true)}
        data-testid="cancel-consultation-button"
        className="transition-opacity disabled:opacity-50"
        disabled={loading || isNavigatorLoading}
      >
        {isNavOnly && consultationType === OTHER_SERVICE.HEALTH_NAVIGATOR ? (
          <RefreshIcon className="text-header-text" />
        ) : (
          <Close customClassName="text-header-text" />
        )}
      </button>
    </>
  )
}
